"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEGACY_FOCUSED_IDENTIFIERS = exports.NO_SYMPTOMS = exports.FOCUSED_SYNTHETIC_OBSERVATIONS = exports.FOCUSED_RULE_STATE_OBSERVATION = exports.FOCUSED_SYMPTOMS_OBSERVATION = exports.FOCUSED_VITAL_SIGNS = void 0;
exports.isFocusedSyntheticObservation = isFocusedSyntheticObservation;
exports.ruleMatches = ruleMatches;
exports.evaluateFocusedEntryPointRules = evaluateFocusedEntryPointRules;
exports.planLeaves = planLeaves;
exports.selectFocusedSteps = selectFocusedSteps;
exports.selectFocusedLeaves = selectFocusedLeaves;
exports.symptomPageStepIds = symptomPageStepIds;
exports.buildFocusedPlan = buildFocusedPlan;
const { compareResponse } = require('@shared/clinical-logic/helpers/compareResponse.js');
exports.FOCUSED_VITAL_SIGNS = [
    'temperature', 'respiratory_rate', 'heart_rate', 'oxygen_saturation', 'blood_pressure',
];
exports.FOCUSED_SYMPTOMS_OBSERVATION = 'focused_symptoms';
exports.FOCUSED_RULE_STATE_OBSERVATION = 'focused_rule_state';
exports.FOCUSED_SYNTHETIC_OBSERVATIONS = [
    exports.FOCUSED_SYMPTOMS_OBSERVATION, exports.FOCUSED_RULE_STATE_OBSERVATION,
    // The removed Additional symptoms answer, still in Text encounters saved before it went; never sent to MRX2.
    'additional_symptoms',
];
exports.NO_SYMPTOMS = 'none';
function isFocusedSyntheticObservation(name) {
    return exports.FOCUSED_SYNTHETIC_OBSERVATIONS.includes(name);
}
exports.LEGACY_FOCUSED_IDENTIFIERS = new Set([
    'rdt_choice', 'rdt_status', 'rdt_bioline_capture_practitioner',
]);
const NOT_RECORDED = new Set(['', 'no', 'none', 'unknown']);
function normalise(value) {
    return String(value).trim().toLowerCase();
}
function ruleMatches(rule, value) {
    if (value === undefined || value === null)
        return false;
    if (rule.operator === 'any')
        return !NOT_RECORDED.has(normalise(value));
    if (rule.operator === 'eq')
        return compareResponse(normalise(value), 'eq', normalise(rule.value));
    const part = rule.component === 'systolic' ? 'n' : rule.component === 'diastolic' ? 'd' : '';
    return compareResponse(String(value), `${part}${rule.operator}`, rule.value);
}
function evaluateFocusedEntryPointRules(rules, values, triggerExclusions = []) {
    const excluded = new Set(triggerExclusions.map(({ symptomId, questionCode }) => `${symptomId}\u0000${questionCode}`));
    const actions = new Map();
    for (const rule of rules) {
        if (excluded.has(`${rule.symptomId}\u0000${rule.questionCode}`))
            continue;
        if (!ruleMatches(rule, values[rule.questionCode]))
            continue;
        const symptomActions = actions.get(rule.symptomId) ?? new Set();
        symptomActions.add(rule.action);
        actions.set(rule.symptomId, symptomActions);
    }
    const assume = [];
    const ask = [];
    for (const [symptomId, symptomActions] of actions) {
        if (symptomActions.has('assume'))
            assume.push(symptomId);
        else if (symptomActions.has('ask'))
            ask.push(symptomId);
    }
    return { assume, ask };
}
function planLeaves(definition) {
    const codeOf = (step) => {
        const conceptId = step.questionId ? definition.questions[step.questionId]?.conceptId : undefined;
        return conceptId ? definition.concepts[conceptId]?.code?.code : undefined;
    };
    const leaves = [];
    (definition.assessment?.steps ?? []).forEach((step, topIndex) => {
        const nested = step.kind === 'group' ? step.steps ?? [] : [step];
        for (const leaf of nested) {
            if (!leaf.id)
                continue;
            leaves.push({
                id: leaf.id,
                code: codeOf(leaf),
                topIndex,
                ...(step.kind === 'group' && step.id ? { groupId: step.id } : {}),
                ...(leaf.sourceGroupId ? { sourceGroupId: leaf.sourceGroupId } : {}),
            });
        }
    });
    return leaves;
}
function selectFocusedSteps(definition, panels) {
    return selectFocusedLeaves(planLeaves(definition), panels);
}
function selectFocusedLeaves(leaves, panels) {
    const selected = new Set();
    const unmappedPanels = [];
    const unmappedResponses = [];
    for (const panel of panels) {
        const candidates = leaves.filter(leaf => ((leaf.sourceGroupId ?? leaf.groupId) === `group.${panel.name}` || leaf.id === `step.${panel.name}`));
        if (!candidates.length) {
            if (!exports.LEGACY_FOCUSED_IDENTIFIERS.has(panel.name))
                unmappedPanels.push(panel.name);
            continue;
        }
        if (!panel.responseFilter) {
            candidates.forEach(leaf => selected.add(leaf.id));
            continue;
        }
        const available = new Set(candidates.map(leaf => leaf.code));
        panel.responseFilter
            .filter(name => !available.has(name))
            .forEach(name => unmappedResponses.push(`${panel.name}.${name}`));
        const requested = new Set(panel.responseFilter);
        candidates.filter(leaf => leaf.code && requested.has(leaf.code)).forEach(leaf => selected.add(leaf.id));
    }
    return {
        stepIds: leaves.filter(leaf => selected.has(leaf.id)).map(leaf => leaf.id),
        unmappedPanels,
        unmappedResponses,
    };
}
function conditionHolds(rule, answers) {
    if ('all' in rule)
        return rule.all.every(child => conditionHolds(child, answers));
    if ('any' in rule)
        return rule.any.some(child => conditionHolds(child, answers));
    const answer = answers[rule.conceptId];
    const numericAnswer = typeof answer === 'number' || (typeof answer === 'string' && answer.trim() !== '')
        ? Number(answer)
        : Number.NaN;
    const numericValue = Number(rule.value);
    const comparable = Number.isFinite(numericAnswer) && Number.isFinite(numericValue);
    switch (rule.operator) {
        case 'eq': return answer === rule.value;
        case 'ne': return answer !== rule.value;
        case 'contains': return Array.isArray(answer) && answer.includes(String(rule.value));
        case 'not_contains': return !Array.isArray(answer) || !answer.includes(String(rule.value));
        case 'gt': return comparable && numericAnswer > numericValue;
        case 'gte': return comparable && numericAnswer >= numericValue;
        case 'lt': return comparable && numericAnswer < numericValue;
        case 'lte': return comparable && numericAnswer <= numericValue;
        default: return false;
    }
}
function symptomPageStepIds(definition, symptom) {
    const symptomOnly = { [symptom.conceptId]: symptom.value };
    const opensWithSymptom = (rule) => Boolean(rule)
        && !conditionHolds(rule, {})
        && conditionHolds(rule, symptomOnly);
    const ids = (steps) => steps.flatMap(step => (step.id ? [step.id] : []));
    const gated = symptom.selectionOnly ? [] : (definition.assessment?.steps ?? []).flatMap(step => {
        if (step.kind !== 'group')
            return opensWithSymptom(step.visibleWhen) ? ids([step]) : [];
        const nested = step.steps ?? [];
        if (step.visibleWhen && !conditionHolds(step.visibleWhen, symptomOnly))
            return [];
        if (opensWithSymptom(step.visibleWhen))
            return ids(nested);
        return ids(nested.filter(child => opensWithSymptom(child.visibleWhen)));
    });
    return Array.from(new Set([...(symptom.followUpStepIds ?? []), ...gated]));
}
function buildFocusedPlan(input) {
    const leaves = planLeaves(input.definition);
    const leafById = new Map(leaves.map(leaf => [leaf.id, leaf]));
    const symptomById = new Map(input.symptoms.map(symptom => [symptom.id, symptom]));
    const values = input.values ?? {};
    const rules = input.vitalSignRules ?? [];
    const triggerExclusions = new Set((input.triggerExclusions ?? [])
        .map(({ symptomId, questionCode }) => `${symptomId}\u0000${questionCode}`));
    const started = [];
    const triggered = [];
    const shown = new Set();
    const shownCodes = new Set();
    const excludedCodes = new Set(input.excludedCodes ?? []);
    const answeredSymptomIds = new Set(input.answeredSymptomIds ?? []);
    const pageSteps = new Map(input.symptoms.map(symptom => [
        symptom.id, symptomPageStepIds(input.definition, symptom),
    ]));
    const trackPosition = (id) => {
        const positions = (pageSteps.get(id) ?? []).map(stepId => leafById.get(stepId)?.topIndex ?? Infinity);
        return positions.length ? Math.min(...positions) : Infinity;
    };
    const inTrackOrder = (ids) => ids
        .map((id, index) => ({ id, index, position: trackPosition(id) }))
        .sort((left, right) => left.position - right.position || left.index - right.index)
        .map(entry => entry.id);
    const newSymptoms = (ids) => Array.from(new Set(ids))
        .filter(id => symptomById.has(id) && !started.includes(id));
    const askable = (leaf) => Boolean(leaf)
        && !shown.has(leaf.id)
        && !(leaf.code && (excludedCodes.has(leaf.code) || shownCodes.has(leaf.code)));
    const show = (stepId) => {
        shown.add(stepId);
        const code = leafById.get(stepId)?.code;
        if (code)
            shownCodes.add(code);
    };
    const vitalLeafByCode = new Map();
    const leafPosition = new Map(leaves.map((leaf, position) => [leaf.id, position]));
    for (const leaf of leaves) {
        if (leaf.code && exports.FOCUSED_VITAL_SIGNS.includes(leaf.code) && !vitalLeafByCode.has(leaf.code)) {
            vitalLeafByCode.set(leaf.code, leaf);
        }
    }
    const vitalPosition = (code) => leafPosition.get(vitalLeafByCode.get(code).id);
    const vitalOrder = (codes) => codes.sort((left, right) => vitalPosition(left) - vitalPosition(right));
    const waves = [];
    let waveIds = inTrackOrder(newSymptoms(input.selectedSymptomIds));
    while (waveIds.length) {
        started.push(...waveIds);
        waveIds.filter(id => !answeredSymptomIds.has(id) && !symptomById.get(id).selectionOnly)
            .forEach(id => excludedCodes.add(symptomById.get(id).code));
        const pages = waveIds.flatMap(id => {
            const stepIds = (pageSteps.get(id) ?? []).filter(stepId => askable(leafById.get(stepId)));
            stepIds.forEach(show);
            return stepIds.length ? [{ symptomId: id, stepIds }] : [];
        });
        const vitalCodes = vitalOrder(Array.from(new Set(waveIds.flatMap(id => symptomById.get(id).minVitalSigns)))
            .filter(code => askable(vitalLeafByCode.get(code))));
        const vitalSignStepIds = vitalCodes.map(code => vitalLeafByCode.get(code).id);
        vitalSignStepIds.forEach(show);
        waves.push({ symptomIds: waveIds, pages, vitalSignStepIds });
        const triggeredNow = newSymptoms(rules
            .filter(rule => vitalCodes.includes(rule.questionCode)
            && !triggerExclusions.has(`${rule.symptomId}\u0000${rule.questionCode}`)
            && ruleMatches(rule, values[rule.questionCode]))
            .map(rule => rule.symptomId));
        triggered.push(...triggeredNow);
        waveIds = inTrackOrder(triggeredNow);
    }
    const focused = new Set(input.focusedStepIds ?? []);
    const remainingStepIds = leaves
        .filter(leaf => focused.has(leaf.id) && askable(leaf))
        .map(leaf => {
        show(leaf.id);
        return leaf.id;
    });
    return {
        waves,
        startedSymptomIds: started,
        triggeredSymptomIds: triggered,
        remainingStepIds,
    };
}
